import axios from 'axios';
import { startSapSync } from '@/Services/sapAdapterService';

/**
 * Vuex module: integrations/sapSync
 * - Triggers sync operations against the SAP adapter using the stored API key.
 * - After every SAP sync (success or failure), calls Franchise Online Server's
 *   /sap/syncSapStock to re-tick getQtyFromPrice = 1 on fuel SKUs for Harvest stores.
 */
const state = () => ({
  syncing: false,
  lastSummaries: {
    items: null,
    debtors: null,
    stockQty: null,
  },
  error: null,
});

const mutations = {
  setSyncing(s, v) { s.syncing = !!v; },
  setSummary(s, { resource, summary }) { s.lastSummaries[resource] = summary || null; },
  setError(s, e) { s.error = e || null; },
};

/**
 * Triggers the re-tick on Franchise Online Server to force getQtyFromPrice = 1
 * on fuel SKUs (PP1001/PP1000) for all Harvest Group stores. This runs AFTER every
 * SAP sync operation regardless of whether the sync succeeded or failed.
 * The re-tick is idempotent, so calling it when already set is harmless.
 */
async function triggerReTick() {
  const baseUrl = window.franchiseTomcatApi || window.stocklistApi;
  if (!baseUrl) {
    console.warn('[syncSapStock] franchiseTomcatApi not configured, skipping re-tick');
    return;
  }
  try {
    await axios.get(`${baseUrl}sap/reTickFuelStockSettings`);
  } catch (e) {
    // Re-tick failure is non-fatal — log but don't throw
    console.warn('[syncSapStock] re-tick call failed:', e?.message);
  }
}

const actions = {
  /**
   * @param context
   * @param {Object} payload
   */
  async syncItems({ commit, rootState }, payload = { mode: 'incremental', pageSize: 100000 }) {
    let result;
    try {
      commit('setSyncing', true);
      const apiKey = rootState.integrations?.sap?.sapApiKey;
      if (!apiKey) throw new Error('SAP adapter not integrated for this store.');
      result = await startSapSync('/sync/items', apiKey, payload);
      commit('setSummary', { resource: 'items', result });
      return result;
    } catch (e) {
      commit('setError', e);
      throw e;
    } finally {
      commit('setSyncing', false);
      // Always re-tick, regardless of sync success or failure
      await triggerReTick();
    }
  },
  async syncDebtors({ commit, rootState }, payload = { mode: 'incremental', pageSize: 100000 }) {
    let result;
    try {
      commit('setSyncing', true);
      const apiKey = rootState.integrations?.sap?.sapApiKey;
      if (!apiKey) throw new Error('SAP adapter not integrated for this store.');
      result = await startSapSync('/sync/debtors', apiKey, payload);
      commit('setSummary', { resource: 'debtors', result });
      return result;
    } catch (e) {
      commit('setError', e);
      throw e;
    } finally {
      commit('setSyncing', false);
      await triggerReTick();
    }
  },
  async syncStockQty({ commit, rootState }, payload = { mode: 'full', pageSize: 100000 }) {
    let result;
    try {
      commit('setSyncing', true);
      const apiKey = rootState.integrations?.sap?.sapApiKey;
      if (!apiKey) throw new Error('SAP adapter not integrated for this store.');
      result = await startSapSync('/sync/stock-qty', apiKey, payload);
      commit('setSummary', { resource: 'stockQty', result });
      return result;
    } catch (e) {
      commit('setError', e);
      throw e;
    } finally {
      commit('setSyncing', false);
      await triggerReTick();
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};